import type {LocationEcoregionData} from "../types";
import {TERRAIN_ELEVATION_BAND_LABELS, type HabitatFamilyLabel, type TerrainElevationBand} from "./constants";

const WATER_DISTANCE_SHORELINE_METERS = 250;
const WATER_DISTANCE_NEAR_METERS = 1500;
const WATER_DISTANCE_CONTEXT_METERS = 5000;

export const normalizeText = (value: unknown) => {
    if (typeof value !== 'string') return '';
    return value.trim().replace(/\s+/g, ' ');
};

const HABITAT_MIX_LABEL_OVERRIDES: Record<string, string> = {
    bare_sparse: 'Bare / sparse',
    bare_sparse_vegetation: 'Bare / sparse vegetation',
    tundra_moss_lichen: 'Tundra / moss / lichen',
};

const getStringList = (value: unknown) => {
    if (Array.isArray(value)) {
        return value
            .map(item => normalizeText(item))
            .filter(Boolean);
    }

    const text = normalizeText(value);
    return text ? [text] : [];
};

export const normalizeSearchText = (value: unknown) => {
    return normalizeText(value).toLowerCase();
};

export const normalizeHabitatMixLabel = (value: unknown) => {
    const normalized = normalizeSearchText(value);
    if (!normalized) return '';

    const override = HABITAT_MIX_LABEL_OVERRIDES[normalized];
    if (override) return override;

    return normalized.replace(/_/g, ' ');
};

export const splitTerms = (value: unknown) => {
    const text = normalizeText(value);
    if (!text) return [];

    return text
        .split(/[|/,;]+/)
        .flatMap(part => part.split(/\band\b/gi))
        .map(part => normalizeSearchText(part))
        .filter(Boolean);
};

const HOTSPOT_NAME_STOP_WORDS = new Set([
    'and',
    'area',
    'auto',
    'ave',
    'bay',
    'blvd',
    'center',
    'centre',
    'co',
    'county',
    'ctr',
    'dr',
    'east',
    'fm',
    'highway',
    'hwy',
    'island',
    'lake',
    'lane',
    'lot',
    'north',
    'park',
    'parking',
    'preserve',
    'rd',
    'reserve',
    'road',
    'site',
    'south',
    'sp',
    'state',
    'st',
    'street',
    'trail',
    'unit',
    'us',
    'view',
    'west'
]);

export const getHotspotNameTerms = (value: unknown) => {
    const text = normalizeText(value);
    if (!text) return [];

    return Array.from(new Set(
        text
            .toLowerCase()
            .replace(/[()]/g, ' ')
            .split(/[^a-z0-9]+/)
            .map(part => part.trim())
            .filter(part => part.length >= 4 && !HOTSPOT_NAME_STOP_WORDS.has(part))
    ));
};

export const getCoordinateTerms = (latitude?: number, longitude?: number) => {
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return [];

    const absoluteLatitude = Math.abs(latitude as number);
    const terms = new Set<string>();

    terms.add((latitude as number) >= 0 ? 'northern hemisphere' : 'southern hemisphere');

    if (absoluteLatitude < 23.5) {
        terms.add('tropical');
        terms.add('low latitude');
    } else if (absoluteLatitude < 35) {
        terms.add('subtropical');
    } else if (absoluteLatitude < 50) {
        terms.add('temperate');
        terms.add('mid latitude');
    } else if (absoluteLatitude < 66.5) {
        if ((latitude as number) >= 0) {
            terms.add('boreal');
            terms.add('subarctic');
        } else {
            terms.add('subantarctic');
        }
        terms.add('high latitude');
    } else {
        if ((latitude as number) >= 0) {
            terms.add('arctic');
        } else {
            terms.add('antarctic');
        }
        terms.add('polar');
    }

    return Array.from(terms);
};

export const uniqueStrings = (values: string[]) => {
    return Array.from(new Set(values.map(value => normalizeText(value)).filter(Boolean)));
};

export const normalizeNumber = (value: unknown) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
};

export const toTitleCase = (value: string) => {
    const normalized = normalizeText(value);
    if (!normalized) return '';

    return normalized
        .split(' ')
        .map(part => {
            if (!part) return part;
            if (part.includes('/')) {
                return part.split('/').map(segment => segment ? `${segment.charAt(0).toUpperCase()}${segment.slice(1).toLowerCase()}` : segment).join('/');
            }

            if (part.includes('-')) {
                return part.split('-').map(segment => segment ? `${segment.charAt(0).toUpperCase()}${segment.slice(1).toLowerCase()}` : segment).join('-');
            }

            return `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`;
        })
        .join(' ');
};

const includesAny = (source: string, terms: string[]) => terms.some(term => source.includes(term));

const classifyLandCoverFamilyValue = (value: unknown): HabitatFamilyLabel | undefined => {
    const normalized = normalizeSearchText(value);
    if (!normalized) return undefined;

    if (includesAny(normalized, ['open water', 'permanent water', 'water body', 'ocean', 'sea', 'lake', 'river', 'shore', 'marsh', 'wetland', 'mangrove', 'swamp', 'estuary', 'lagoon'])) {
        return 'Water';
    }

    if (includesAny(normalized, ['forest', 'woodland', 'tree', 'forestland', 'broadleaf', 'needleleaf', 'conifer'])) {
        return 'Forest';
    }

    if (includesAny(normalized, ['shrubland', 'scrub', 'brush', 'chaparral', 'sagebrush', 'heath'])) {
        return 'Shrubland';
    }

    if (includesAny(normalized, ['grassland', 'savanna', 'prairie', 'meadow', 'steppe', 'pasture', 'herbaceous'])) {
        return 'Grassland';
    }

    if (includesAny(normalized, ['desert', 'xeric', 'arid', 'dune', 'badland'])) {
        return 'Desert';
    }

    if (includesAny(normalized, ['urban', 'built-up', 'built up', 'settlement', 'suburban'])) {
        return 'Urban';
    }

    if (includesAny(normalized, ['cropland', 'agricultural', 'farm', 'field', 'plantation'])) {
        return 'Agricultural';
    }

    if (includesAny(normalized, ['tundra', 'alpine', 'montane', 'subalpine', 'boreal', 'highland', 'mountain'])) {
        return 'Mountain';
    }

    if (includesAny(normalized, ['wetland'])) {
        return 'Wetland';
    }

    return undefined;
};

export const slugifyHabitatLabel = (value: string) => {
    return normalizeText(value)
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
};

export const getElevationBand = (value: unknown, fallbackElevation?: number): TerrainElevationBand | undefined => {
    const normalizedBand = normalizeSearchText(value).replace(/\s+/g, '_');
    if (normalizedBand && (TERRAIN_ELEVATION_BAND_LABELS as Record<string, string>)[normalizedBand]) {
        return normalizedBand as TerrainElevationBand;
    }

    const elevation = normalizeNumber(fallbackElevation);
    if (elevation === undefined) return undefined;

    if (elevation < 0) return 'below_sea_level';
    if (elevation < 500) return 'lowland';
    if (elevation < 1500) return 'foothills';
    if (elevation < 2500) return 'montane';
    if (elevation < 3500) return 'subalpine';
    return 'alpine';
};

export const getTerrainSummary = (band?: TerrainElevationBand) => {
    if (!band) return undefined;
    return `${TERRAIN_ELEVATION_BAND_LABELS[band]} terrain`;
};

export const getWaterContext = (proximityLabel?: unknown, distanceMeters?: unknown) => {
    const label = normalizeText(proximityLabel);
    if (label) return label;

    const distance = normalizeNumber(distanceMeters);
    if (distance === undefined) return undefined;

    if (distance <= WATER_DISTANCE_SHORELINE_METERS) return 'On the shoreline';
    if (distance <= WATER_DISTANCE_NEAR_METERS) return 'Near water';
    if (distance <= WATER_DISTANCE_CONTEXT_METERS) return 'Water-influenced landscape';
    return 'Inland';
};

export const getBiomeTerms = (value: unknown) => {
    return splitTerms(value);
};

export const getEcoregionTerms = (value: unknown) => {
    return splitTerms(value);
};

export const getLandCoverFamily = (values: Array<unknown>) => {
    for (const value of values) {
        const family = classifyLandCoverFamilyValue(value);
        if (family) {
            return family;
        }
    }

    return 'Mixed';
};

export const getPrimaryHabitatLabel = (values: Array<unknown>, fallbackFamily: HabitatFamilyLabel) => {
    const normalized = values.map(normalizeText).filter(Boolean);
    const candidate = normalized[0] || '';
    if (candidate) return toTitleCase(candidate);

    switch (fallbackFamily) {
        case 'Water':
            return 'Open Water';
        case 'Coastal':
            return 'Coastal Habitat';
        case 'Wetland':
            return 'Wetland';
        case 'Forest':
            return 'Forest';
        case 'Shrubland':
            return 'Shrubland';
        case 'Grassland':
            return 'Grassland';
        case 'Desert':
            return 'Desert';
        case 'Urban':
            return 'Urban Green Space';
        case 'Agricultural':
            return 'Agricultural Edge';
        case 'Mountain':
            return 'Mountain Habitat';
        default:
            return 'Mixed Habitat';
    }
};

export const getPreferredHabitatPrimary = (ecoData?: LocationEcoregionData) => {
    if (!ecoData) return '';

    return normalizeText(
        ecoData.habitat_local_primary
        || ecoData.habitat_primary
        || ecoData.habitat_group
        || ecoData.worldcover_label
    );
};

export const getPreferredHabitatSecondary = (ecoData?: LocationEcoregionData) => {
    if (!ecoData) return [];

    const preferredSecondary = getStringList(ecoData.habitat_local_secondary);
    if (preferredSecondary.length) {
        return uniqueStrings(preferredSecondary);
    }

    const legacySecondary = getStringList(ecoData.habitat_secondary)
        .flatMap(value => splitTerms(value));

    return uniqueStrings(legacySecondary);
};

export const getPreferredHabitatMix = (ecoData?: LocationEcoregionData) => {
    if (!ecoData) return undefined;

    if (ecoData.habitat_local_mix && typeof ecoData.habitat_local_mix === 'object' && !Array.isArray(ecoData.habitat_local_mix)) {
        return ecoData.habitat_local_mix;
    }

    return ecoData.habitat_mix;
};

export const getProfileConfidence = (context: {
    primaryHabitat?: string;
    secondaryHabitats?: string[];
    terrainSummary?: string;
    waterContext?: string;
    coordinateTerms?: string[];
    biomeTerms?: string[];
    ecoregionTerms?: string[];
}) => {
    const signalCount = [
        context.primaryHabitat,
        context.secondaryHabitats?.length ? 'secondary' : '',
        context.terrainSummary,
        context.waterContext,
        context.coordinateTerms?.length ? 'coordinates' : '',
        context.biomeTerms?.length ? 'biome' : '',
        context.ecoregionTerms?.length ? 'ecoregion' : ''
    ].filter(Boolean).length;

    if (signalCount >= 4) return 'high';
    if (signalCount >= 2) return 'medium';
    return 'low';
};

export const makeReasonList = (context: {
    primaryHabitat?: string;
    secondaryHabitats?: string[];
    terrainSummary?: string;
    waterContext?: string;
    hotspotNameTerms?: string[];
    coordinateTerms?: string[];
    biomeTerms?: string[];
    ecoregionTerms?: string[];
}) => {
    const reasons: string[] = [];

    if (context.primaryHabitat) {
        reasons.push(`Primary habitat: ${context.primaryHabitat}`);
    }

    if (context.secondaryHabitats?.length) {
        reasons.push(`Secondary habitats: ${context.secondaryHabitats.slice(0, 3).join(', ')}`);
    }

    if (context.terrainSummary) {
        reasons.push(`Terrain: ${context.terrainSummary}`);
    }

    if (context.waterContext) {
        reasons.push(`Water context: ${context.waterContext}`);
    }

    if (context.hotspotNameTerms?.length) {
        reasons.push(`Hotspot name terms: ${context.hotspotNameTerms.slice(0, 3).join(', ')}`);
    }

    if (context.coordinateTerms?.length) {
        reasons.push(`Coordinate terms: ${context.coordinateTerms.slice(0, 3).join(', ')}`);
    }

    if (context.biomeTerms?.length) {
        reasons.push(`Biome terms: ${context.biomeTerms.slice(0, 3).join(', ')}`);
    }

    if (context.ecoregionTerms?.length) {
        reasons.push(`Ecoregion terms: ${context.ecoregionTerms.slice(0, 3).join(', ')}`);
    }

    return reasons;
};
